/**
 * OptimizerTab — PyPortfolioOpt-equivalent portfolio optimization UI.
 * Pulls 1y daily closes for every symbol in the watchlist, computes
 * log returns + covariance in-browser, runs the chosen optimizer, and
 * renders the resulting weights as a horizontal bar chart with the
 * portfolio's expected return / vol / Sharpe alongside.
 */
import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { LazyPlot as Plot } from '@/components/LazyPlot';
import { useStore } from '@/store';
import { api } from '@/lib/api';
import { logReturns, optimize, type OptimMethod } from '@/lib/portfolioOptimizer';
import type { Bar } from '@/types';
import { ORG, GRN, RED, MUT, FONT } from '@/theme';

const METHODS: Array<[OptimMethod, string]> = [
  ['max_sharpe',   'MAX SHARPE'],
  ['min_variance', 'MIN VARIANCE'],
  ['risk_parity',  'INVERSE VOL'],   // labelled honestly: this is 1/σ weighting, not true risk parity
];

export function OptimizerTab() {
  const symbols = useStore(s => s.watchlist);
  const [method, setMethod] = useState<OptimMethod>('max_sharpe');

  const queries = useQueries({
    queries: symbols.map(sym => ({
      queryKey: ['bars-opt', sym],
      queryFn:  () => api<Bar[]>(`/api/ohlcv/${encodeURIComponent(sym)}?period=1y&interval=1d`),
      staleTime: 600_000,
      retry: false,   // futures (ES=F …) 404 on Alpaca — don't hang LOADING on retries
    })),
  });

  const allLoaded = queries.every(q => !q.isLoading);

  const result = useMemo(() => {
    if (!allLoaded) return null;
    const returns = queries.map(q => logReturns((q.data ?? []).map(b => b.close)));
    // Drop symbols with too few observations so the optimizer doesn't choke.
    const filtered = symbols.map((s, i) => ({ s, r: returns[i] })).filter(x => x.r.length >= 30);
    if (filtered.length < 2) return null;
    // Align every series to the same length (most-recent N observations). Ragged
    // inputs make the covariance matrix ill-formed (NaN / singular), which was
    // throwing out of covMatrix before the optimizer's own guard could catch it.
    const minLen  = Math.min(...filtered.map(x => x.r.length));
    const aligned = filtered.map(x => ({ s: x.s, r: x.r.slice(x.r.length - minLen) }));
    try {
      return optimize(aligned.map(x => x.s), aligned.map(x => x.r), method);
    } catch {
      return null;   // never crash the tab — show the empty state instead
    }
  }, [allLoaded, queries, symbols, method]);

  if (!allLoaded) return <Cent>LOADING PRICE HISTORY...</Cent>;
  if (!result)    return <Cent>NOT ENOUGH DATA TO OPTIMIZE</Cent>;

  return (
    <div style={{ fontFamily: FONT, color: '#e0e0e0' }}>
      {/* Method pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
        <span style={{ color: MUT, fontSize: 10, letterSpacing: 1 }}>OBJECTIVE:</span>
        {METHODS.map(([v, lbl]) => (
          <button key={v} onClick={() => setMethod(v)} style={{
            background: method === v ? ORG : '#1a1a1a',
            color: method === v ? '#000' : '#888',
            border: '1px solid ' + (method === v ? ORG : '#333'),
            fontFamily: FONT, fontSize: 10, padding: '3px 10px',
            borderRadius: 2,
          }}>{lbl}</button>
        ))}
      </div>

      {/* The optimiser can fall back to equal weights when the covariance
          matrix is singular or no long-only tangency portfolio exists.
          That fallback used to be indistinguishable from a real result. */}
      {result.degenerate && (
        <div style={{
          border: '1px solid ' + ORG, background: 'rgba(255,153,0,0.08)',
          padding: '6px 10px', marginBottom: 8, fontSize: 10.5, color: ORG, lineHeight: 1.45,
        }}>
          <strong>NOT AN OPTIMISATION — EQUAL WEIGHTS SHOWN.</strong>{' '}
          <span style={{ color: MUT }}>{result.note}</span>
        </div>
      )}

      {/* Portfolio stats */}
      <div style={{
        display: 'flex', gap: 18, marginBottom: 8,
        padding: '4px 0', borderBottom: '1px solid #222', fontSize: 11,
      }}>
        <Stat label="EXP RET"    value={Number.isFinite(result.expectedReturn) ? `${(result.expectedReturn * 100).toFixed(2)}%` : '—'}
              color={result.expectedReturn >= 0 ? GRN : RED} />
        <Stat label="EXP VOL"    value={Number.isFinite(result.expectedVolatility) ? `${(result.expectedVolatility * 100).toFixed(2)}%` : '—'} />
        <Stat label="SHARPE"     value={Number.isFinite(result.sharpe) ? result.sharpe.toFixed(2) : '—'}
              color={!Number.isFinite(result.sharpe) ? MUT : result.sharpe >= 1 ? GRN : result.sharpe >= 0 ? ORG : RED} />
        <Stat label="ASSETS"     value={String(result.symbols.length)} />
        <Stat label="RISK FREE"  value="4.5%" />
      </div>

      {/* Weights bar chart */}
      <div style={{ height: 280 }}>
        <Plot
          data={[{
            type: 'bar', orientation: 'h',
            x: result.weights.map(w => w * 100),
            y: result.symbols,
            text: result.weights.map(w => `${(w * 100).toFixed(1)}%`),
            textposition: 'outside',
            marker: { color: ORG },
            hovertemplate: '%{y}: %{x:.2f}%<extra></extra>',
          } as unknown as Plotly.Data]}
          layout={{
            paper_bgcolor: '#090909', plot_bgcolor: '#090909',
            margin: { l: 60, r: 60, t: 8, b: 30 },
            font: { color: '#e0e0e0', family: 'Consolas, monospace', size: 10 },
            xaxis: { gridcolor: '#1a1a1a', color: MUT, ticksuffix: '%' },
            yaxis: { color: '#e0e0e0', tickfont: { size: 10 } },
            showlegend: false,
          } as unknown as Partial<Plotly.Layout>}
          config={{ displayModeBar: false, responsive: true }}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler
        />
      </div>

      <div style={{ fontSize: 10, color: MUT, marginTop: 6 }}>
        Long-only, fully invested. Optimised on 1Y daily log returns. Risk-free
        4.5%. Weights below 0 clipped + renormalised.
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div style={{ color: MUT, fontSize: 9, letterSpacing: 1 }}>{label}</div>
      <div style={{ color: color ?? '#e0e0e0', fontWeight: 'bold', fontSize: 11 }}>{value}</div>
    </div>
  );
}

function Cent({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', color: '#333', fontSize: 11, letterSpacing: 2, fontFamily: FONT,
    }}>{children}</div>
  );
}
